import { css, SerializedStyles } from '@emotion/core'
import * as React from 'react'

import { Link } from './Link'
import TextCardContent from './TextCardContent'

export interface TextCardProps {
  className?: string
  title?: string
  blueSubtitle?: string
  graySubtitle?: string
  description: string
  footnote?: string
  iconSrc?: string
  imageSrc?: string
  gatsbyImageSrc?: any
  hoverImageSrc?: string
  roundedImage?: boolean
  imgWithBorder?: boolean
  linkText?: string
  linkUrl?: string
  arrowLinkUrl?: string
  additionalJsx?: React.ReactNode
  isCentered?: boolean
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
  iconAboveImageSrc?: string
  withImageOverlay?: boolean
  cssStyles?: SerializedStyles | SerializedStyles[]
  withCenteredImageOnGrayBG?: boolean
  bgColor?: 'blue' | 'green' | 'peach' | 'purple'
  headingClassName?: string
  imgStyle?: any
  withoutBorderRadius?: boolean
}

const TextCard: React.FunctionComponent<TextCardProps> = ({
  className,
  title,
  blueSubtitle,
  graySubtitle,
  description,
  footnote,
  iconSrc,
  imageSrc,
  gatsbyImageSrc,
  imgStyle,
  imgWithBorder = false,
  hoverImageSrc,
  roundedImage = false,
  linkText,
  linkUrl,
  arrowLinkUrl,
  additionalJsx,
  isCentered = false,
  onClick,
  iconAboveImageSrc,
  withImageOverlay,
  cssStyles = css``,
  withCenteredImageOnGrayBG = false,
  bgColor,
  headingClassName = 'heading4',
  withoutBorderRadius = false,
}) => {
  const clickable = !!linkUrl || !!linkText

  return (
    <div
      className={className}
      css={[containerStyle(isCentered), cssStyles, (withCenteredImageOnGrayBG || bgColor) && grayBgStyle(bgColor)]}
      onClick={onClick}
    >
      {clickable ? (
        <Link className="translate-and-shadow-on-hover" href={linkUrl} css={clickableContainerStyle}>
          <TextCardContent
            title={title}
            blueSubtitle={blueSubtitle}
            graySubtitle={graySubtitle}
            description={description}
            footnote={footnote}
            gatsbyImageSrc={gatsbyImageSrc}
            iconSrc={iconSrc}
            imageSrc={imageSrc}
            imgStyle={imgStyle}
            imgWithBorder={imgWithBorder}
            hoverImageSrc={hoverImageSrc}
            roundedImage={roundedImage}
            linkText={linkText}
            arrowLinkUrl={arrowLinkUrl}
            additionalJsx={additionalJsx}
            isCentered={isCentered}
            onClick={onClick}
            iconAboveImageSrc={iconAboveImageSrc}
            withImageOverlay={withImageOverlay}
            clickable={clickable}
            headingClassName={headingClassName}
            withoutBorderRadius={withoutBorderRadius}
          />
        </Link>
      ) : (
        <TextCardContent
          title={title}
          blueSubtitle={blueSubtitle}
          graySubtitle={graySubtitle}
          description={description}
          footnote={footnote}
          gatsbyImageSrc={gatsbyImageSrc}
          imageSrc={imageSrc}
          imgStyle={imgStyle}
          imgWithBorder={imgWithBorder}
          hoverImageSrc={hoverImageSrc}
          roundedImage={roundedImage}
          linkText={linkText}
          arrowLinkUrl={arrowLinkUrl}
          additionalJsx={additionalJsx}
          isCentered={isCentered}
          onClick={onClick}
          iconAboveImageSrc={iconAboveImageSrc}
          withImageOverlay={withImageOverlay}
          clickable={clickable}
          headingClassName={headingClassName}
          withoutBorderRadius={withoutBorderRadius}
        />
      )}
    </div>
  )
}

export default TextCard

const containerStyle = (isCentered?: boolean) => css`
  text-align: left;
  height: 100%;

  ${isCentered &&
  `
    text-align: center;
    `};
`

const clickableContainerStyle = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 0.25rem;
  overflow: hidden;
`

const grayBgStyle = (bgColor?: string) => css`
  & > div {
    height: 100%;
  }

  #text-card-image {
    ${bgColor
      ? `
          background-size: cover;
          background-repeat: no-repeat;
          background-image: url('/images/${bgColor}-gradient-bg.webp');
        `
      : `
          background: #fafafa;
          border: 1px solid #efeff0;
          border-bottom: none;
        `}
    aspect-ratio: 2/1;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem;
  }

  #text-card-content {
    border: 1px solid #efeff0;
    border-top: none;
    padding: 1.5rem;
    border-radius: 0 0 0.25rem 0.25rem;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
`
